/**
 * Note-level placeholder aggregation.
 *
 * Each note is rendered as multiple section editors, each running the bracket
 * highlight plugin. This module hoists the per-section bracket matches up to a
 * single note-level view so the UI can show how many placeholders remain and
 * jump between them, and so signing can be soft-gated until they are filled.
 *
 * The aggregator state is owned by the note container (NoteChatView) via
 * `useNotePlaceholderAggregator`; section items report their matches up through
 * a thin context (`useReportSectionPlaceholders`) to avoid prop-drilling
 * through the sections list.
 */
/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { BracketMatch } from '@components/BlockNoteBracketHighlightPlugin';

import { LAYOUT_SELECTORS } from '../constants/layout';

export type ReportSectionPlaceholders = (
  sectionId: string,
  matches: BracketMatch[],
) => void;

const noop: ReportSectionPlaceholders = () => {};

const ReporterContext = createContext<ReportSectionPlaceholders>(noop);

export const NotePlaceholderReporterProvider = ReporterContext.Provider;

/**
 * Hook for a section editor to report its current bracket placeholder matches.
 * Returns a no-op when used outside a provider (e.g. Storybook, tests).
 */
export const useReportSectionPlaceholders = (): ReportSectionPlaceholders =>
  useContext(ReporterContext);

export interface NotePlaceholderEntry {
  sectionId: string;
  match: BracketMatch;
}

interface AggregatorOptions {
  /** Section ids in document order, so entries are traversed top-to-bottom. */
  orderedSectionIds?: string[];
}

export interface NotePlaceholderAggregator {
  /** Pass this down through NotePlaceholderReporterProvider. */
  reportSectionBrackets: ReportSectionPlaceholders;
  /** All outstanding placeholders across the note, in document order. */
  entries: NotePlaceholderEntry[];
  /** Convenience: entries.length. */
  total: number;
  /**
   * Scroll the next outstanding placeholder into view and open its
   * replace-text popover (cycles through all placeholders).
   */
  jumpToNext: () => void;
}

const sameMatches = (a: BracketMatch[], b: BracketMatch[]): boolean =>
  a.length === b.length &&
  a.every(
    (m, i) => m.from === b[i].from && m.to === b[i].to && m.text === b[i].text,
  );

/**
 * Owns the note-level aggregation of outstanding bracket placeholders.
 * Call in the note container; provide `reportSectionBrackets` to children.
 */
export function useNotePlaceholderAggregator({
  orderedSectionIds,
}: AggregatorOptions): NotePlaceholderAggregator {
  const [bySection, setBySection] = useState<Record<string, BracketMatch[]>>({});

  const reportSectionBrackets = useCallback<ReportSectionPlaceholders>(
    (sectionId, matches) => {
      setBySection(prev => {
        const existing = prev[sectionId];
        if (matches.length === 0) {
          if (!existing) return prev;
          const next = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete next[sectionId];
          return next;
        }
        if (existing && sameMatches(existing, matches)) return prev;
        return { ...prev, [sectionId]: matches };
      });
    },
    [],
  );

  const entries = useMemo<NotePlaceholderEntry[]>(() => {
    const ordered = (orderedSectionIds ?? []).filter(
      id => bySection[id]?.length,
    );
    // Include any reported sections not present in the order list (safety).
    const extra = Object.keys(bySection).filter(
      id => bySection[id]?.length && !ordered.includes(id),
    );
    const result: NotePlaceholderEntry[] = [];
    for (const id of [...ordered, ...extra]) {
      const sorted = [...(bySection[id] ?? [])].sort((a, b) => a.from - b.from);
      for (const match of sorted) result.push({ sectionId: id, match });
    }
    return result;
  }, [bySection, orderedSectionIds]);

  // Cycle through the rendered bracket placeholders. Driven off the DOM (the
  // decoration spans the bracket plugin renders) rather than ProseMirror
  // positions: it reliably scrolls the outer note container, works regardless
  // of the editor registry, and lets us reuse the existing per-bracket "Replace
  // text" popover by replaying the click sequence ProseMirror's handleClick
  // listens for (mousedown → mouseup).
  const cursorRef = useRef(0);
  const jumpToNext = useCallback(() => {
    const root = document.querySelector(LAYOUT_SELECTORS.NOTE_SECTIONS_CONTAINER);
    if (!root) return;
    const spans = Array.from(
      root.querySelectorAll<HTMLElement>(
        '.bracket-highlight[data-clickable="true"]',
      ),
    );
    if (spans.length === 0) return;
    if (cursorRef.current >= spans.length) cursorRef.current = 0;
    const span = spans[cursorRef.current];
    cursorRef.current = (cursorRef.current + 1) % spans.length;

    // Instant scroll so the span's coordinates are valid for the click below.
    span.scrollIntoView({ block: 'center', behavior: 'auto' });

    const rect = span.getBoundingClientRect();
    const opts: MouseEventInit = {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX: rect.left + Math.min(rect.width / 2, 8),
      clientY: rect.top + rect.height / 2,
      button: 0,
    };
    span.dispatchEvent(new MouseEvent('mousedown', opts));
    span.dispatchEvent(new MouseEvent('mouseup', opts));
  }, []);

  return {
    reportSectionBrackets,
    entries,
    total: entries.length,
    jumpToNext,
  };
}
